import * as React from "react";
import { createBrowserRouter, Navigate, Outlet } from "react-router";
import { LoginRoute } from "@/features/auth/login-route";
import { VerifyRoute } from "@/features/auth/verify-route";
import { AppShell } from "@/components/app-shell";
import { useAuth } from "@/features/auth/auth-context";
import { DashboardRoute } from "@/features/dashboard/dashboard-route";
import { RoomsListRoute } from "@/features/rooms/rooms-list-route";
import { RoomDetailRoute } from "@/features/rooms/room-detail-route";
import { EntryCreateRoute } from "@/features/entries/entry-create-route";
import { SammelModusRoute } from "@/features/entries/sammel-modus-route";
import { WikiIndexRoute } from "@/features/wiki/wiki-index-route";
import { WikiArticleRoute } from "@/features/wiki/wiki-article-route";
import { TeamRoute } from "@/features/settings/team-route";
import { TraegerGate } from "@/features/traeger/traeger-gate";
import { TraegerDashboardRoute } from "@/features/traeger/traeger-dashboard-route";
import { TraegerOrgsRoute } from "@/features/traeger/traeger-orgs-route";
import { TraegerExportRoute } from "@/features/traeger/traeger-export-route";

// Phase 16.D — leaflet-vendor chunks bleiben lazy bis user die routes öffnet
const TraegerCompareRoute = React.lazy(() => import("@/features/traeger/traeger-compare-route"));
const TraegerMapRoute = React.lazy(() => import("@/features/traeger/traeger-map-route"));

// Phase 20.B — statistik + heatmap im selben lazy-chunk
const StatistikRoute = React.lazy(() => import("@/features/statistik/statistik-route"));

// Phase 20.D — aushang print-view + qrcode.react lazy im selben chunk
const AushangDetailRoute = React.lazy(() => import("@/features/aushang/aushang-detail-route"));

// Phase 20 — settings lazy (war eager, mit 20-CTAs zu groß für main-bundle)
const SettingsRoute = React.lazy(() => import("@/features/settings/settings-route"));

// Phase 18 — signup-wizard lazy, weil anonyme user meistens /login hitten;
// signup ist seltener gefragt → kein grund das im main-bundle zu haben.
const SignupWizard = React.lazy(() =>
  import("@/features/signup/signup-wizard").then((m) => ({ default: m.SignupWizard })),
);
const SignupConfirmRoute = React.lazy(() =>
  import("@/features/signup/signup-confirm-route").then((m) => ({ default: m.SignupConfirmRoute })),
);

function LazyRoute({ children }: { children: React.ReactNode }): React.ReactElement {
  return (
    <React.Suspense
      fallback={
        <div className="flex items-center justify-center py-12 text-sm text-neutral-500">
          Lade Ansicht …
        </div>
      }
    >
      {children}
    </React.Suspense>
  );
}

/**
 * AuthGate — guards the authenticated tree. While the AuthContext is
 * loading (dexie read race), we render a thin "loading"-shell to avoid
 * a flash of /login when the user has a valid session.
 */
function AuthGate(): React.ReactElement {
  const auth = useAuth();
  if (auth.status === "loading") {
    return (
      <main className="flex min-h-screen items-center justify-center text-sm text-neutral-500">
        Lade …
      </main>
    );
  }
  if (auth.status === "anonymous") {
    return <Navigate to="/login" replace />;
  }
  return (
    <AppShell>
      <Outlet />
    </AppShell>
  );
}

/**
 * AnonGate — bounces back to /dashboard wenn die session schon steht.
 * Vermeidet "ich seh die login-page kurz nach refresh"-effekte.
 */
function AnonGate(): React.ReactElement {
  const auth = useAuth();
  if (auth.status === "authenticated") {
    return <Navigate to="/dashboard" replace />;
  }
  return <Outlet />;
}

export const router = createBrowserRouter([
  {
    element: <AnonGate />,
    children: [
      { path: "/login", element: <LoginRoute /> },
      { path: "/auth/verify", element: <VerifyRoute /> },
      {
        path: "/registrieren",
        element: (
          <LazyRoute>
            <SignupWizard />
          </LazyRoute>
        ),
      },
      {
        path: "/registrieren/bestaetigen",
        element: (
          <LazyRoute>
            <SignupConfirmRoute />
          </LazyRoute>
        ),
      },
    ],
  },
  {
    element: <AuthGate />,
    children: [
      { index: true, element: <Navigate to="/dashboard" replace /> },
      { path: "/dashboard", element: <DashboardRoute /> },
      { path: "/raeume", element: <RoomsListRoute /> },
      { path: "/raeume/:id", element: <RoomDetailRoute /> },
      { path: "/entries/neu", element: <EntryCreateRoute /> },
      { path: "/entries/sammel", element: <SammelModusRoute /> },
      { path: "/wiki", element: <WikiIndexRoute /> },
      { path: "/wiki/:slug", element: <WikiArticleRoute /> },
      { path: "/einstellungen", element: <LazyRoute><SettingsRoute /></LazyRoute> },
      { path: "/einstellungen/team", element: <TeamRoute /> },
      // Phase 20.B — statistik route (lazy, NOT in license-gate ALLOW-list per STATISTIK-UX.md §2)
      {
        path: "/statistik",
        element: (
          <LazyRoute>
            <StatistikRoute />
          </LazyRoute>
        ),
      },
      // Phase 20.D — aushang print-view (lazy, NOT in license-gate ALLOW-list per AUSHANG-UX.md §2)
      {
        path: "/aushang/:roomId",
        element: (
          <LazyRoute>
            <AushangDetailRoute />
          </LazyRoute>
        ),
      },
      // Phase 15.D — Träger-Portal (T-Tier, role traeger_admin)
      {
        path: "/traeger",
        element: <TraegerGate />,
        children: [
          { index: true, element: <Navigate to="/traeger/dashboard" replace /> },
          { path: "dashboard", element: <TraegerDashboardRoute /> },
          { path: "orgs", element: <TraegerOrgsRoute /> },
          {
            path: "vergleich",
            element: (
              <LazyRoute>
                <TraegerCompareRoute />
              </LazyRoute>
            ),
          },
          {
            path: "karte",
            element: (
              <LazyRoute>
                <TraegerMapRoute />
              </LazyRoute>
            ),
          },
          { path: "export", element: <TraegerExportRoute /> },
        ],
      },
    ],
  },
  {
    path: "*",
    element: <Navigate to="/dashboard" replace />,
  },
]);
